import {openResourceSection} from '../../core/resourceEvents';
import {
	AP_ALARMS_SECTION_EVENT,
	AP_INTERFACE_SECTION_EVENT,
	AP_PACKET_CAPTURE_SECTION_EVENT,
	AP_RADIO_SETTINGS_SECTION_EVENT,
	AP_ROGUE_SECTION_EVENT,
} from './events';

/**
 * The accordions on an Access Point's own dashboard.
 *
 * An acknowledged event rather than `settleExistingSection`, which is what
 * the Maintenance and IO-Pass tabs use. The difference is the markup: those
 * tabs carry `data-agent-section` and can be found by label, and these
 * accordions do not - the dashboard opens one by setting its own state. So
 * the page reports back, the same way it already does for the SSID
 * accordion, and reporting back is also what lets these be recorded as
 * verified rather than merely registered.
 *
 * Two of the three are conditional: Packet Capture and Interface show only
 * while the Access Point is connected. A page that cannot open one calls
 * `fail()`, and the reply says the section was unavailable instead of
 * claiming it opened.
 */
const ROGUE_SECTIONS = new Set(['ap.rogue_ap']);
const PACKET_CAPTURE_SECTIONS = new Set(['ap.packet_capture']);
const INTERFACE_SECTIONS = new Set(['ap.interface']);

/**
 * Open the Rogue AP accordion on an access point dashboard.
 * @return {Promise<object>} completion payload
 */
export function openRogueApSection() {
	return openResourceSection(
		AP_ROGUE_SECTION_EVENT, 'ap.rogue_ap', ROGUE_SECTIONS,
	);
}

/**
 * Open the Packet Capture accordion on an access point dashboard.
 * @return {Promise<object>} completion payload
 */
export function openPacketCaptureSection() {
	return openResourceSection(
		AP_PACKET_CAPTURE_SECTION_EVENT,
		'ap.packet_capture',
		PACKET_CAPTURE_SECTIONS,
	);
}

/**
 * Open the Interface accordion on an access point dashboard.
 * @return {Promise<object>} completion payload
 */
export function openApInterfaceSection() {
	return openResourceSection(
		AP_INTERFACE_SECTION_EVENT, 'ap.interface', INTERFACE_SECTIONS,
	);
}

const ALARMS_SECTIONS = new Set(['ap.alarms']);
const RADIO_SETTINGS_SECTIONS = new Set(['ap.radio_settings']);

/**
 * Open the Alarms accordion on an access point dashboard.
 *
 * This access point's own alarms, not the Maintenance list - the same
 * distinction `switch.alarm_list` already makes on a switch.
 * @return {Promise<object>} completion payload
 */
export function openApAlarmsSection() {
	return openResourceSection(
		AP_ALARMS_SECTION_EVENT, 'ap.alarms', ALARMS_SECTIONS,
	);
}

/**
 * Open the Radio Settings accordion on an access point dashboard.
 *
 * The read view. `ap.radio` opens the editable form beside it, and the page
 * refuses that without permission to change radios - so this is what
 * answers a request to see the settings rather than change one.
 * @return {Promise<object>} completion payload
 */
export function openApRadioSettingsSection() {
	return openResourceSection(
		AP_RADIO_SETTINGS_SECTION_EVENT,
		'ap.radio_settings',
		RADIO_SETTINGS_SECTIONS,
	);
}
